import * as React from 'react';
import { cva } from 'class-variance-authority';
import { AlertCircle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

const alertVariants = cva('relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4', {
  variants: {
    variant: {
      default: 'bg-card text-foreground border-border',
      success: 'border-success/30 bg-success/5 text-foreground [&>svg]:text-success',
      warning: 'border-warning/30 bg-warning/5 text-foreground [&>svg]:text-warning',
      error: 'border-error/30 bg-error/5 text-foreground [&>svg]:text-error',
      info: 'border-info/30 bg-info/5 text-foreground [&>svg]:text-info',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const icons = {
  default: Info,
  success: CheckCircle2,
  warning: AlertCircle,
  error: XCircle,
  info: Info,
};

const Alert = React.forwardRef(({ className, variant = 'default', dismissible, onDismiss, children, ...props }, ref) => {
  const Icon = icons[variant] || Info;
  return (
    <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props}>
      <Icon className="h-4 w-4" />
      <div>{children}</div>
      {dismissible && (
        <button
          type="button"
          onClick={onDismiss}
          className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
});
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h5 ref={ref} className={cn('mb-1 font-medium leading-none tracking-tight', className)} {...props} />
));
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('text-sm text-muted-foreground [&_p]:leading-relaxed', className)} {...props} />
));
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription };
